import type { Place, ViewState } from "../../types/warehouse.types";
import { Size, PlaceColumn, PlaceRendererConfig } from "../core/types";

/**
 * Context passed through the render pipeline
 */
export interface RenderContext {
  // Input data
  places: Map<string, Place>;
  viewState: ViewState;
  activeLevels: Set<number>;
  activeRows: Set<string>;
  selectedPlaceIds: Set<string>;
  canvasSize?: Size;
  config: PlaceRendererConfig;

  // Canvas context
  ctx: CanvasRenderingContext2D;
  worldToScreen: (x: number, y: number) => { x: number; y: number };

  // Pipeline state (populated by phases)
  columns?: PlaceColumn[];
  sortedColumns?: PlaceColumn[];
  visibleColumns?: PlaceColumn[];
  globalMaxLevel?: number;

  // Subsystems (injected)
  [key: string]: any;
}

/**
 * Base interface for render phases
 *
 * Pattern: Chain of Responsibility + Command Pattern
 */
export interface RenderPhase {
  /**
   * Execute this phase
   */
  execute(context: RenderContext): void;

  /**
   * Determine if this phase can be skipped
   */
  canSkip(context: RenderContext): boolean;

  /**
   * Get phase name for debugging
   */
  getName(): string;
}

/**
 * Phase 1: Cache Maintenance
 */
export class MaintenancePhase implements RenderPhase {
  getName(): string {
    return 'Maintenance';
  }

  canSkip(): boolean {
    return false; // Always run
  }

  execute(ctx: RenderContext): void {
    // Run maintenance on sprite factory
    if (ctx.spriteFactory?.runMaintenance) {
      ctx.spriteFactory.runMaintenance();
    }

    // Run maintenance on layout cache if needed
    if (ctx.layoutCache?.runMaintenance) {
      ctx.layoutCache.runMaintenance();
    }
  }
}

/**
 * Phase 2: Data Preparation (Grouping into columns)
 */
export class PreparationPhase implements RenderPhase {
  getName(): string {
    return 'Preparation';
  }

  canSkip(ctx: RenderContext): boolean {
    // Skip if we have dirty tracking and nothing changed
    if (ctx.dirtyTracker) {
      return !ctx.dirtyTracker.isDirty('data') && !!ctx.columns;
    }
    return false;
  }

  execute(ctx: RenderContext): void {
    if (!ctx.columnBuilder) {
      throw new Error('columnBuilder not provided in context');
    }

    const result = ctx.columnBuilder.build(
      ctx.places,
      ctx.activeLevels,
      ctx.activeRows
    );

    ctx.columns = result.columns;
    ctx.globalMaxLevel = result.globalMaxLevel;
  }
}

/**
 * Phase 3: Sorting (Painter's Algorithm)
 */
export class SortingPhase implements RenderPhase {
  getName(): string {
    return 'Sorting';
  }

  canSkip(ctx: RenderContext): boolean {
    // Skip if viewport hasn't changed (rotation is part of viewport)
    if (ctx.dirtyTracker && ctx.sortedColumns) {
      return !ctx.dirtyTracker.isDirty('viewport');
    }
    return false;
  }

  execute(ctx: RenderContext): void {
    if (!ctx.columns) {
      throw new Error('Columns not prepared');
    }

    if (!ctx.columnSorter) {
      throw new Error('columnSorter not provided in context');
    }

    ctx.sortedColumns = ctx.columnSorter.sort(ctx.columns, ctx.viewState);
  }
}

/**
 * Phase 4: Culling (Filter visible columns)
 */
export class CullingPhase implements RenderPhase {
  getName(): string {
    return 'Culling';
  }

  canSkip(ctx: RenderContext): boolean {
    return !ctx.canvasSize; // Skip if no canvas size provided
  }

  execute(ctx: RenderContext): void {
    if (!ctx.sortedColumns) {
      throw new Error('Columns not sorted');
    }

    if (!ctx.culler) {
      // Fallback: use all columns if no culler
      ctx.visibleColumns = ctx.sortedColumns;
      return;
    }

    ctx.visibleColumns = ctx.culler.cull(
      ctx.sortedColumns,
      ctx.viewState,
      ctx.worldToScreen,
      ctx.canvasSize!,
      ctx.layoutCache,
      ctx.globalMaxLevel || 1,
      ctx.config
    );
  }
}

/**
 * Phase 5: Drawing
 */
export class DrawingPhase implements RenderPhase {
  getName(): string {
    return 'Drawing';
  }

  canSkip(): boolean {
    return false; // Always draw
  }

  execute(ctx: RenderContext): void {
    if (!ctx.visibleColumns && !ctx.sortedColumns) {
      throw new Error('No columns to draw');
    }

    if (!ctx.placeDrawer) {
      throw new Error('placeDrawer not provided in context');
    }

    const columnsToDraw = ctx.visibleColumns || ctx.sortedColumns!;

    ctx.placeDrawer.draw(
      ctx.ctx,
      columnsToDraw,
      ctx.globalMaxLevel || 1,
      ctx.viewState,
      ctx.worldToScreen,
      ctx.selectedPlaceIds,
      ctx.canvasSize
    );
  }
}

/**
 * Render Pipeline orchestrator
 *
 * Pattern: Chain of Responsibility
 * Purpose: Execute rendering in well-defined phases
 */
export class RenderPipeline {
  private phases: RenderPhase[];
  private stats = {
    executedPhases: [] as string[],
    skippedPhases: [] as string[],
    totalTime: 0,
    phaseTimings: new Map<string, number>(),
  };

  constructor(phases?: RenderPhase[]) {
    this.phases = phases || [
      new MaintenancePhase(),
      new PreparationPhase(),
      new SortingPhase(),
      new CullingPhase(),
      new DrawingPhase(),
    ];
  }

  /**
   * Execute the full pipeline
   */
  execute(context: RenderContext): void {
    const startTime = performance.now();
    this.stats.executedPhases = [];
    this.stats.skippedPhases = [];
    this.stats.phaseTimings.clear();

    for (const phase of this.phases) {
      const phaseName = phase.getName();

      if (phase.canSkip(context)) {
        this.stats.skippedPhases.push(phaseName);
        continue;
      }

      const phaseStart = performance.now();
      phase.execute(context);
      const phaseTime = performance.now() - phaseStart;

      this.stats.executedPhases.push(phaseName);
      this.stats.phaseTimings.set(phaseName, phaseTime);
    }

    this.stats.totalTime = performance.now() - startTime;
  }

  /**
   * Add a phase to the pipeline
   */
  addPhase(phase: RenderPhase, position?: number): void {
    if (position !== undefined) {
      this.phases.splice(position, 0, phase);
    } else {
      this.phases.push(phase);
    }
  }

  /**
   * Remove a phase by name
   */
  removePhase(name: string): boolean {
    const index = this.phases.findIndex(p => p.getName() === name);
    if (index !== -1) {
      this.phases.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Replace a phase
   */
  replacePhase(name: string, newPhase: RenderPhase): boolean {
    const index = this.phases.findIndex(p => p.getName() === name);
    if (index !== -1) {
      this.phases[index] = newPhase;
      return true;
    }
    return false;
  }

  /**
   * Get pipeline statistics
   */
  getStats(): {
    executedPhases: string[];
    skippedPhases: string[];
    totalTime: number;
    phaseTimings: Map<string, number>;
  } {
    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      executedPhases: [],
      skippedPhases: [],
      totalTime: 0,
      phaseTimings: new Map(),
    };
  }
}
